import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { DisplayMode } from "modules/streaming/constants";

export type Resolutions =
  | "1280x960"
  | "1280x720"
  | "640x480"
  | "640x360"
  | "320x240"
  | "320x180"
  | undefined;

export type FrameRates = 30 | 15 | 7 | 1 | undefined;

export type QualityOptions = keyof Quality;

export interface QualityOption {
  resolution: Resolutions;
  frameRate: FrameRates;
}

export interface Quality {
  High: QualityOption;
  Normal: QualityOption;
  Low: QualityOption;
  Default: QualityOption;
}

export enum ConnectionStatus {
  CONNECTED = "connected",
  CONNECTING = "connecting",
  DISCONNECTED = "disconnected",
  ERROR = "error",
  PENDING = "pending",
  RECONNECTING = "reconnecting",
}

export enum ContentHint {
  detail = "detail",
  motion = "motion",
  notSet = "",
  text = "text",
}

export interface StreamingReducerState {
  isCameraOn: boolean;
  isMicrophoneOn: boolean;
  isTalking: boolean;
  isScreenSharing: boolean;
  isStreaming: boolean;
  isSpeakerOn: boolean;
  displayMode: DisplayMode;
  screenSharingCount: number;
  userSetQuality: QualityOptions;
  connectionStatus: ConnectionStatus;
  connectionAttempts: number;
  contentHint: {
    screen: ContentHint;
    video: ContentHint;
  };
  preferredFrameRate: FrameRates;
  preferredResolution: Resolutions;
  micOnDuration: number;
}

export type ContentHintDevice = keyof StreamingReducerState["contentHint"];

export const streamingReducerInitialState: StreamingReducerState = {
  isCameraOn: false,
  isMicrophoneOn: false,
  isTalking: false,
  isScreenSharing: false,
  isStreaming: false,
  isSpeakerOn: true,
  displayMode: DisplayMode.MAP,
  screenSharingCount: 0,
  connectionStatus: ConnectionStatus.PENDING,
  connectionAttempts: 0,
  contentHint: {
    video: ContentHint.notSet,
    screen: ContentHint.notSet,
  },
  preferredFrameRate: undefined,
  preferredResolution: "1280x720",
  userSetQuality: "Default",
  micOnDuration: 0,
};

export const streamingSlice = createSlice({
  name: "streaming",
  initialState: streamingReducerInitialState,
  reducers: {
    updateIsStreaming: (state, action) => {
      state.isStreaming = action.payload;
    },

    updateScreenSharingCount: (state, action: PayloadAction<number>) => {
      state.screenSharingCount = action.payload;
    },

    startScreenSharing: (state, action: PayloadAction<ContentHint>) => {
      state.contentHint.screen = action.payload;
      state.isScreenSharing = true;
    },

    stopScreenSharing: (state) => {
      state.isScreenSharing = false;
    },

    switchToMapMode: (state) => {
      state.displayMode = DisplayMode.MAP;
    },

    switchToSpeakerMode: (state) => {
      state.displayMode = DisplayMode.SPEAKER;
    },

    switchToTileMode: (state) => {
      state.displayMode = DisplayMode.TILE;
    },

    switchToContentTileMode: (state) => {
      state.displayMode = DisplayMode.CONTENT_TILE;
    },

    switchToScreenFocusMode: (state) => {
      state.displayMode = DisplayMode.SCREEN_FOCUS;
    },

    turnOnCamera: (state) => {
      state.isCameraOn = true;
    },
    turnOffCamera: (state) => {
      state.isCameraOn = false;
    },
    turnOnMicrophone: (state) => {
      state.isMicrophoneOn = true;
    },
    turnOffMicrophone: (state) => {
      state.isMicrophoneOn = false;
    },
    setIsMicrophoneOn: (state, action: PayloadAction<boolean>) => {
      state.isMicrophoneOn = action.payload;
    },
    setIsTalking: (state, action: PayloadAction<boolean>) => {
      state.isTalking = action.payload;
    },
    setIsCameraOn: (state, action: PayloadAction<boolean>) => {
      state.isCameraOn = action.payload;
    },
    turnOnSpeaker: (state) => {
      state.isSpeakerOn = true;
    },
    turnOffSpeaker: (state) => {
      state.isSpeakerOn = false;
    },
    turnOffStreams: (state) => {
      state.isMicrophoneOn = false;
      state.isCameraOn = false;
      state.isScreenSharing = false;
    },
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      if (action.payload === ConnectionStatus.CONNECTED) {
        state.connectionAttempts = 0;
      }
      state.connectionStatus = action.payload;
    },
    incrementConnectionAttempts: (state) => {
      state.connectionAttempts += 1;
    },
    setVideoContentHint: (state, action: PayloadAction<ContentHint>) => {
      state.contentHint.video = action.payload;
    },
    setScreenContentHint: (state, action: PayloadAction<ContentHint>) => {
      state.contentHint.screen = action.payload;
    },
    setConnectionAttempts: (state, action: PayloadAction<number>) => {
      state.connectionAttempts = action.payload;
    },
    setQuality: (state, action: PayloadAction<QualityOptions>) => ({
      ...state,
      userSetQuality: action.payload,
    }),
    setResolutionAndFrameRate: (
      state,
      action: PayloadAction<QualityOption>,
    ) => ({
      ...state,
      preferredFrameRate: action.payload.frameRate,
      preferredResolution: action.payload.resolution,
    }),
    increaseMicOnDurationBy: (state, action: PayloadAction<number>) => {
      state.micOnDuration += action.payload;
    },
    resetMicOnDuration: (state) => {
      state.micOnDuration = 0;
    },
    resetConnectionStatus: (state) => {
      state.connectionStatus = ConnectionStatus.PENDING;
      state.connectionAttempts = 0;
    },
    resetStreaming: () => streamingReducerInitialState,
  },
});

export const {
  switchToMapMode,
  startScreenSharing,
  stopScreenSharing,
  switchToScreenFocusMode,
  switchToSpeakerMode,
  switchToTileMode,
  switchToContentTileMode,
  updateIsStreaming,
  turnOnCamera,
  turnOffCamera,
  updateScreenSharingCount,
  turnOnMicrophone,
  turnOffMicrophone,
  turnOnSpeaker,
  turnOffSpeaker,
  turnOffStreams,
  setConnectionStatus,
  incrementConnectionAttempts,
  setConnectionAttempts,
  resetConnectionStatus,
  setIsCameraOn,
  setIsMicrophoneOn,
  setIsTalking,
  setScreenContentHint,
  setVideoContentHint,
  resetStreaming,
  setQuality,
  setResolutionAndFrameRate,
  increaseMicOnDurationBy,
  resetMicOnDuration,
} = streamingSlice.actions;

export default streamingSlice.reducer;
